/**
 * GalleryCustomizeControls — the curated knobs, as a controlled form.
 *
 * GAL-11. Accent (design default / your brand colour / custom hex),
 * headline + body fonts from a short list, slideshow on/off + seconds.
 * GALLERY-2 adds the handoff's theme tweaks:
 *   Background — the design's own ground ("Default") or one of five;
 *                00 Basic has its own five, White being its default.
 *   Type       — font pairings, as presets over the same `fonts` the
 *                per-face pickers write; "Custom" opens those pickers.
 *   Cover      — 00 Basic only: its cover's height and dim.
 * Purely controlled: `value` in, `onChange(next)` out. The creation
 * flow holds the value in local state until Create; the editor's
 * Design section persists every change to the row. Neither knows
 * about the other.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  backgroundsFor,
  pairingFor,
  pairingsFor,
  type GalleryFontPairing,
  type GalleryTheme,
} from '../templates/themes';
import {
  SLIDESHOW_DEFAULT_SECONDS,
  SLIDESHOW_MAX_SECONDS,
  SLIDESHOW_MIN_SECONDS,
  clampSlideshowSeconds,
  COVER_DIM_MAX,
  COVER_DIM_MIN,
  COVER_HEIGHT_MAX,
  COVER_HEIGHT_MIN,
  clampCoverDim,
  clampCoverHeight,
  GALLERY_BACKGROUND_IDS,
  GALLERY_BAR_MODES,
  BAR_MODE_USES_LOGO,
  clampBarMode,
  type GalleryBarMode,
  isGalleryBackgroundId,
  DISPLAY_FONTS,
  BODY_FONTS,
  HEX_COLOR,
  GALLERY_GRAINS,
  isGalleryGrain,
  clampFocusX,
  clampFocusY,
  clampCoverLayout,
  GALLERY_COVER_LAYOUTS,
  COVER_LAYOUT_USES,
  type GalleryBackgroundId,
  type GalleryCoverLayout,
  type GalleryCustomization,
} from '../templates/types';

// SEC-XSS — these now live in templates/types.ts, beside the validator
// that enforces them. Re-exported so existing imports keep working, and
// so the picker can never drift from the whitelist: a face the list
// doesn't contain is stripped on the way back in, which would look like
// the control silently doing nothing.
export { DISPLAY_FONTS, BODY_FONTS } from '../templates/types';

const DEFAULT = '__default__';
const HEX = HEX_COLOR;
/** The Background chip for "the design's own ground" (no key stored). */
const ORIGINAL = 'original';
/** The Type chip for a hand-picked mix (the per-face pickers). */
const CUSTOM = 'custom';
/** Basic's cover sliders move in fives, like the handoff's. */
const COVER_STEP = 5;

/** The accent a design will actually render with, given the knobs. */
export const effectiveAccent = (
  theme: GalleryTheme,
  value: GalleryCustomization,
  brandColor: string | null,
): string =>
  value.useBrandColor && brandColor ? brandColor : (value.accent ?? theme.accent);

export function GalleryCustomizeControls({
  theme,
  value,
  onChange,
  brandColor,
  brandLogoUrl = null,
  disabled = false,
  className,
  coverUrl,
}: {
  theme: GalleryTheme;
  value: GalleryCustomization;
  onChange: (next: GalleryCustomization) => void;
  brandColor: string | null;
  /** The logo the gallery will actually draw, or null if none is set. */
  brandLogoUrl?: string | null;
  /** The gallery's cover photo, so the focal picker has something to
   *  aim at. Callers without photos get an empty frame and a hint. */
  coverUrl?: string | null;
  disabled?: boolean;
  className?: string;
}) {
  const templateId = theme.id;
  const isBasic = templateId === 'basic';

  const useBrand = !!value.useBrandColor;
  const customAccent = value.accent ?? null;
  const accent = effectiveAccent(theme, value, brandColor);

  // Hex field: local draft, committed (debounced) only when valid.
  const [hex, setHex] = useState(customAccent ?? theme.accent);
  useEffect(() => setHex(customAccent ?? theme.accent), [customAccent, theme.accent]);
  const hexTimer = useRef<number | null>(null);
  const onHex = (v: string) => {
    setHex(v);
    if (!HEX.test(v)) return;
    if (hexTimer.current) window.clearTimeout(hexTimer.current);
    hexTimer.current = window.setTimeout(
      () => onChange({ ...value, accent: v, useBrandColor: false }),
      350,
    );
  };
  useEffect(() => () => { if (hexTimer.current) window.clearTimeout(hexTimer.current); }, []);

  const slideshowOn = value.slideshow?.enabled ?? true;
  const seconds = clampSlideshowSeconds(value.slideshow?.seconds ?? SLIDESHOW_DEFAULT_SECONDS);
  const [secondsDraft, setSecondsDraft] = useState(seconds);
  useEffect(() => setSecondsDraft(seconds), [seconds]);

  const setFont = (which: 'display' | 'body', family: string | null) => {
    // A face picked by hand keeps the pickers open, even if the mix
    // happens to match a pairing (as on iOS).
    setCustomOpen(true);
    onChange({ ...value, fonts: { ...value.fonts, [which]: family } });
  };

  // ── Background (GALLERY-2) ──────────────────────────────────────
  // "Default" — and, on Basic, its own White — is the design as drawn,
  // so it is stored as no key at all rather than as a colour.
  const grounds = backgroundsFor(templateId);
  const storedGround = isGalleryBackgroundId(value.background) ? value.background : null;
  const groundKey: GalleryBackgroundId | typeof ORIGINAL = isBasic
    ? (storedGround ?? 'light')
    : (storedGround ?? ORIGINAL);
  // GALLERY-ONE — a free colour. It wins over the keyed ground wherever
  // it's read, so picking a preset has to clear it or the preset would
  // silently do nothing.
  const customHex = typeof value.backgroundHex === 'string' ? value.backgroundHex : null;
  const setBackground = (key: GalleryBackgroundId | typeof ORIGINAL) => {
    if (key === groundKey && !customHex) return;
    const next: GalleryCustomization = { ...value };
    delete next.backgroundHex;
    if (key === ORIGINAL || (isBasic && key === 'light')) delete next.background;
    else next.background = key;
    onChange(next);
  };
  const setCustomHex = (hex: string) => {
    const next: GalleryCustomization = { ...value };
    next.backgroundHex = hex;
    delete next.background;
    onChange(next);
  };

  // ── Type (GALLERY-2) ────────────────────────────────────────────
  // A pairing is a preset over `fonts`; "Custom" is the per-face
  // pickers. Faces that match no preset ARE custom, so the pickers show.
  const pairings = pairingsFor(templateId);
  const matched = pairingFor(templateId, value.fonts);
  const [customOpen, setCustomOpen] = useState(false);
  useEffect(() => setCustomOpen(false), [templateId]);
  const typeKey = customOpen || !matched ? CUSTOM : matched.id;
  const setPairing = (p: GalleryFontPairing) => {
    if (!customOpen && matched?.id === p.id) return;
    setCustomOpen(false);
    const next: GalleryCustomization = { ...value };
    if (!p.display && !p.body) delete next.fonts;
    else next.fonts = { display: p.display, body: p.body };
    onChange(next);
  };
  const typeHint =
    typeKey === CUSTOM
      ? 'Choose each face below.'
      : `${matched?.display ?? theme.display.family} headlines · ${matched?.body ?? theme.body.family} text`;

  // ── Cover (GALLERY-2, Basic) ────────────────────────────────────
  const coverHeight = clampCoverHeight(value.cover?.height);
  const coverDim = clampCoverDim(value.cover?.dim);
  const [heightDraft, setHeightDraft] = useState(coverHeight);
  const [dimDraft, setDimDraft] = useState(coverDim);
  useEffect(() => setHeightDraft(coverHeight), [coverHeight]);
  useEffect(() => setDimDraft(coverDim), [coverDim]);
  const coverFocusX = clampFocusX(value.cover?.focusX);
  const coverFocusY = clampFocusY(value.cover?.focusY);
  const coverLayout = clampCoverLayout(value.cover?.layout);
  const coverUses = COVER_LAYOUT_USES[coverLayout];
  /**
   * The ONE writer for the cover, and it always sends every field.
   *
   * Each control used to send only its own: moving the height slider
   * wrote { height, dim } and nothing else, so the focal point the
   * studio had just dragged was dropped on the next nudge of a
   * different slider. A patch over the resolved values can't do that.
   * (`??` and not `||`, because 0 is a real focal point and a real dim.)
   */
  /**
   * GALLERY-BAR — one writer, both fields, for the same reason
   * `setCover` below is one: a control that rebuilds the struct from
   * its own draft drops whatever it does not name, and nothing in the
   * type system says so. Three bugs in this file have been that.
   */
  const barMode = clampBarMode(value.bar?.mode);
  const barLogo = value.bar?.logo !== false;
  const setBar = (patch: Partial<NonNullable<GalleryCustomization['bar']>>) =>
    onChange({
      ...value,
      bar: {
        mode: clampBarMode(patch.mode ?? barMode),
        logo: patch.logo ?? barLogo,
      },
    });

  const setCover = (patch: Partial<NonNullable<GalleryCustomization['cover']>>) =>
    onChange({
      ...value,
      cover: {
        height: clampCoverHeight(patch.height ?? coverHeight),
        dim: clampCoverDim(patch.dim ?? coverDim),
        focusX: clampFocusX(patch.focusX ?? coverFocusX),
        focusY: clampFocusY(patch.focusY ?? coverFocusY),
        layout: clampCoverLayout(patch.layout ?? coverLayout),
      },
    });

  return (
    // `gal-customize` repoints --primary at Orbit blue for this subtree
    // only (see library.css). Under the Googie shell --primary is the
    // mustard, which made every switch and slider here gold. Rennan
    // wants these on the signature blue; the rest of Googie keeps its
    // gold, which is the point of scoping it rather than editing
    // googie.css.
    <div className={cn('gal-customize divide-y divide-border/40', className)}>
      {/* Background */}
      <Section label="Background" hint="The page behind your photos.">
        {/* GALLERY-ONE — sixteen grounds, so swatches rather than
            sixteen labelled pills. The name of the chosen one sits
            under the row, which is the only one you need to read. */}
        <div role="group" aria-label="Background" className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {!isBasic && (
              <Swatch
                selected={groundKey === ORIGINAL}
                disabled={disabled}
                color={theme.bg}
                label="Default"
                onClick={() => setBackground(ORIGINAL)}
              />
            )}
            {GALLERY_BACKGROUND_IDS.map((id) => (
              <Swatch
                key={id}
                selected={!customHex && groundKey === id}
                disabled={disabled}
                color={grounds[id].bg}
                label={grounds[id].label}
                onClick={() => setBackground(id)}
              />
            ))}
            {/* Any colour. The sixteen above are a starting point, not
                the limit — the ink, secondary text and hairline are
                derived from whatever is chosen, so it stays readable. */}
            <label
              className={cn(
                'relative h-7 w-7 shrink-0 cursor-pointer rounded-full transition-all',
                'ring-1 ring-inset ring-foreground/15',
                customHex && 'ring-2 ring-primary ring-offset-2 ring-offset-card',
                disabled && 'pointer-events-none opacity-40',
              )}
              style={
                customHex
                  ? { background: customHex }
                  : { background: 'conic-gradient(#e5484d,#f5a524,#f2d600,#46a758,#3b7dd8,#8e4ec6,#e5484d)' }
              }
              title="Any colour"
            >
              <input
                type="color"
                value={customHex ?? '#ffffff'}
                onChange={(e) => setCustomHex(e.target.value)}
                disabled={disabled}
                aria-label="Pick any background colour"
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {customHex
              ? customHex.toUpperCase()
              : groundKey === ORIGINAL
                ? 'Default'
                : grounds[groundKey as GalleryBackgroundId]?.label ?? 'Default'}
          </p>
        </div>
        {/* GALLERY-ONE — grain, on the ground only. Three named steps:
            a studio wants "a bit" or "a lot", and a percentage invites
            fiddling with something that reads the same across ten
            points of it. */}
        <div role="group" aria-label="Grain" className="space-y-2 pt-4">
          <p className="text-xs text-foreground">Grain</p>
          <div className="flex flex-wrap gap-2">
            {GALLERY_GRAINS.map((g) => {
              const current = isGalleryGrain(value.grain) ? value.grain : 'none';
              return (
                <Chip
                  key={g}
                  selected={current === g}
                  disabled={disabled}
                  onClick={() => {
                    const next = { ...value };
                    if (g === 'none') delete next.grain;
                    else next.grain = g;
                    onChange(next);
                  }}
                >
                  {g === 'none' ? 'None' : g === 'fine' ? 'Fine' : 'Heavy'}
                </Chip>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Over the page, never over the photographs.
          </p>
        </div>
      </Section>

      {/* Type — the pairings; Custom opens the per-face pickers */}
      <Section label="Type" hint={typeHint}>
        <ChipRow label="Type">
          {pairings.map((p) => (
            <Chip key={p.id} selected={typeKey === p.id} disabled={disabled} onClick={() => setPairing(p)}>
              {p.label}
            </Chip>
          ))}
          <Chip selected={typeKey === CUSTOM} disabled={disabled} onClick={() => setCustomOpen(true)}>
            Custom
          </Chip>
        </ChipRow>
        {typeKey === CUSTOM && (
          <div className="grid gap-4 pt-1 sm:grid-cols-2">
            <FontPick
              label="Headline"
              value={value.fonts?.display ?? null}
              defaultFamily={theme.display.family}
              options={DISPLAY_FONTS}
              disabled={disabled}
              onChange={(v) => setFont('display', v)}
            />
            <FontPick
              label="Body"
              value={value.fonts?.body ?? null}
              defaultFamily={theme.body.family}
              options={BODY_FONTS}
              disabled={disabled}
              onChange={(v) => setFont('body', v)}
            />
          </div>
        )}
      </Section>

      {/* Accent — not on Basic, which is ink on its ground and wears no accent */}
      {!isBasic && (
        <div className="py-5 first:pt-0 last:pb-0 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-xs">Accent</Label>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {useBrand && brandColor
                  ? 'Using your brand colour.'
                  : customAccent
                    ? 'Custom colour.'
                    : `${theme.name}'s own accent.`}
              </p>
            </div>
            <span className="h-6 w-6 shrink-0 rounded-full border border-border/60" style={{ backgroundColor: accent }} aria-hidden />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-foreground">
              <Switch
                checked={useBrand}
                disabled={disabled || !brandColor}
                onCheckedChange={(v) => onChange({ ...value, useBrandColor: v })}
                title={!brandColor ? 'Set a brand colour in Settings first' : undefined}
              />
              Use my brand colour
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={HEX.test(hex) ? hex : theme.accent}
                onChange={(e) => onHex(e.target.value)}
                disabled={disabled || useBrand}
                className="h-8 w-10 rounded-md border border-border/60 bg-transparent p-0.5 disabled:opacity-40"
                aria-label="Custom accent colour"
              />
              <Input
                value={hex}
                onChange={(e) => onHex(e.target.value)}
                disabled={disabled || useBrand}
                className="h-8 w-28 font-mono text-xs"
                spellCheck={false}
              />
              {(customAccent || useBrand) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 rounded-full text-xs"
                  disabled={disabled}
                  onClick={() => onChange({ ...value, accent: null, useBrandColor: false })}
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* GALLERY-BAR — the strip across the top. Above Cover because
          that is the order a visitor meets them in. */}
      {isBasic && (
        <Section
          label="Top bar"
          hint="What sits above the cover. Favorites, Share and Download stay available whichever you pick."
        >
          <div className="grid grid-cols-3 gap-2">
            {GALLERY_BAR_MODES.map((id) => (
              <BarModeCard
                key={id}
                id={id}
                selected={barMode === id}
                disabled={disabled}
                onClick={() => setBar({ mode: id })}
              />
            ))}
          </div>
          <label className="flex items-center justify-between gap-3 pt-1">
            <span className="min-w-0">
              <span className="block text-xs text-foreground">Show my logo</span>
              <span className="mt-0.5 block text-[11px] text-muted-foreground">
                {!BAR_MODE_USES_LOGO[barMode]
                  ? 'No bar to put it in — the actions float on their own.'
                  : brandLogoUrl
                    ? 'From your brand settings.'
                    : 'Set a logo in Settings → Brand first. Until then your studio name shows on its own.'}
              </span>
            </span>
            <Switch
              checked={barLogo && BAR_MODE_USES_LOGO[barMode] && !!brandLogoUrl}
              disabled={disabled || !BAR_MODE_USES_LOGO[barMode] || !brandLogoUrl}
              onCheckedChange={(v) => setBar({ logo: v })}
            />
          </label>
        </Section>
      )}

      {/* Cover (00 Basic) */}
      {isBasic && (
        <Section label="Cover" hint="Your cover photo, with the gallery's name — the first photo until you set one.">
          {/* GALLERY-ONE — four arrangements. Drawn rather than named,
              because a 24pt wireframe says what it is faster than
              "masthead" does. */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {GALLERY_COVER_LAYOUTS.map((id) => (
              <CoverLayoutCard
                key={id}
                id={id}
                selected={coverLayout === id}
                disabled={disabled}
                onClick={() => setCover({ layout: id })}
              />
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Framed and Masthead show the whole frame — nothing is cropped.
          </p>
          <SliderRow
            label={coverUses.focus ? 'Cover height' : 'Cover size'}
            value={heightDraft}
            min={COVER_HEIGHT_MIN}
            max={COVER_HEIGHT_MAX}
            readout={
              coverUses.focus
                ? `${heightDraft}% of the screen`
                : `up to ${heightDraft}% of the screen`
            }
            disabled={disabled}
            onDraft={(v) => setHeightDraft(v ?? coverHeight)}
            onCommit={(v) => setCover({ height: v })}
          />
          {/* Shown only where they do something. The focal point means
              nothing when the whole frame is on the page, and the dim is
              there to keep type legible over a photograph — which only
              Full bleed does. */}
          {coverUses.focus && (
            <FocalPicker
              coverUrl={coverUrl}
              disabled={disabled}
              x={coverFocusX}
              y={coverFocusY}
              onChange={(x, y) => setCover({ focusX: x, focusY: y })}
            />
          )}
          {coverUses.dim && (
            <SliderRow
              label="Cover dim"
              value={dimDraft}
              min={COVER_DIM_MIN}
              max={COVER_DIM_MAX}
              readout={`${dimDraft}%`}
              disabled={disabled}
              onDraft={(v) => setDimDraft(v ?? coverDim)}
              onCommit={(v) => setCover({ dim: v })}
            />
          )}
        </Section>
      )}

      {/* Slideshow (photo designs) */}
      {theme.kind !== 'video' && (
        <div className="py-5 first:pt-0 last:pb-0 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-xs">Slideshow</Label>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                A play button in the header that walks through every photo.
              </p>
            </div>
            <Switch
              checked={slideshowOn}
              disabled={disabled}
              onCheckedChange={(v) => onChange({ ...value, slideshow: { enabled: v, seconds } })}
            />
          </div>
          {slideshowOn && (
            <div className="flex items-center gap-4">
              <Slider
                value={[secondsDraft]}
                min={SLIDESHOW_MIN_SECONDS}
                max={SLIDESHOW_MAX_SECONDS}
                step={1}
                disabled={disabled}
                onValueChange={(v) => setSecondsDraft(v[0] ?? seconds)}
                onValueCommit={(v) => onChange({ ...value, slideshow: { enabled: true, seconds: v[0] ?? seconds } })}
                className="max-w-xs"
              />
              <span className="w-24 text-xs tabular-nums text-muted-foreground">{secondsDraft}s per photo</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** One titled block of the form, between the hairlines. */
function Section({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="py-5 first:pt-0 last:pb-0 space-y-3">
      <div>
        <Label className="text-xs">{label}</Label>
        {hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

/**
 * GALLERY-ONE — one of the four cover arrangements, as a drawing.
 *
 * A wireframe rather than a word: the grey block is the photograph, the
 * two bars are the gallery's name and its line of detail, and where they
 * sit is literally where they sit on the page. "Masthead" needs a
 * caption to be understood; a picture of a masthead doesn't.
 *
 * Monochrome on purpose. These cards are next to sixteen colour swatches
 * and a colour picker, and a coloured diagram here would read as another
 * palette choice instead of a layout one.
 */
const COVER_LAYOUT_NAMES: Record<GalleryCoverLayout, string> = {
  full: 'Full bleed',
  frame: 'Framed',
  split: 'Split',
  masthead: 'Masthead',
};

const BAR_MODE_NAMES: Record<GalleryBarMode, string> = {
  full: 'Logo & name',
  mark: 'Logo only',
  float: 'No bar',
};

/**
 * Drawn rather than named, like the cover's. A 24pt wireframe of a
 * strip with a dot on the left says what "Logo only" means faster
 * than the words do.
 */
function BarModeCard({
  id, selected, disabled, onClick,
}: {
  id: GalleryBarMode; selected: boolean; disabled: boolean; onClick: () => void;
}) {
  const chip = 'rounded-[1px] bg-foreground/45';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        // `rounded-[10px]` and not `rounded-lg` — see CoverLayoutCard
        // below for the whole story. The shell forces a pill radius on
        // any `.g-form button` whose classes don't carry an excluded
        // one, and `rounded-lg` is not excluded.
        'group flex flex-col gap-1.5 rounded-[10px] border p-2 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:pointer-events-none disabled:opacity-50',
        selected ? 'border-primary bg-foreground/[0.05]' : 'border-border/60 hover:border-foreground/40',
      )}
    >
      <span aria-hidden className="block aspect-[4/3] w-full overflow-hidden rounded-[3px] bg-muted/50 p-1">
        <span className="flex h-full w-full flex-col gap-1">
          {id === 'float' ? (
            // No strip: the photo runs to the top and the actions sit
            // over the corner of it.
            <span className="relative h-full w-full rounded-[2px] bg-foreground/25">
              <span className="absolute right-[3px] top-[3px] flex gap-[2px]">
                <span className={cn(chip, 'h-[3px] w-[5px] bg-background/90')} />
                <span className={cn(chip, 'h-[3px] w-[7px] bg-background/90')} />
              </span>
            </span>
          ) : (
            <>
              <span className="flex h-[22%] w-full items-center gap-1">
                <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-foreground/45" />
                {id === 'full' && <span className={cn(chip, 'h-[3px] w-1/4')} />}
                <span className="flex-1" />
                <span className={cn(chip, 'h-[3px] w-[6px]')} />
                <span className={cn(chip, 'h-[3px] w-[9px]')} />
              </span>
              <span className="w-full flex-1 rounded-[2px] bg-foreground/25" />
            </>
          )}
        </span>
      </span>
      <span className={cn('text-[11px] leading-tight', selected ? 'font-medium text-foreground' : 'text-muted-foreground')}>
        {BAR_MODE_NAMES[id]}
      </span>
    </button>
  );
}

function CoverLayoutCard({
  id, selected, disabled, onClick,
}: {
  id: GalleryCoverLayout; selected: boolean; disabled: boolean; onClick: () => void;
}) {
  const photo = 'rounded-[2px] bg-foreground/25';
  const bar = 'rounded-full bg-foreground/45';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        // `rounded-[10px]`, not `rounded-lg`. Under the Googie shell
        // `.g-form button` is forced to `border-radius: 999px` unless
        // its class list carries an excluded radius, and `rounded-lg`
        // is not on that list — so these cards rendered as CIRCLES with
        // a square wireframe sitting on top of each one (Rennan: "the
        // UI on the cover section, is broken looks bad"). An arbitrary
        // value is excluded, and cannot fall off the list later.
        'group flex flex-col gap-1.5 rounded-[10px] border p-2 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:pointer-events-none disabled:opacity-50',
        // The selected state deliberately avoids `bg-primary/…`: that
        // class is what the shell's aria-pressed rule keys on, and it
        // repaints the tile with its own wash and ring. The border and
        // the label carry the selection; the diagram stays grey so it
        // keeps reading as a diagram.
        selected ? 'border-primary bg-foreground/[0.05]' : 'border-border/60 hover:border-foreground/40',
      )}
    >
      <span aria-hidden className="block aspect-[4/3] w-full overflow-hidden rounded-[3px] bg-muted/50 p-1">
        {id === 'full' && (
          <span className={cn(photo, 'relative flex h-full w-full flex-col items-center justify-center gap-1')}>
            <span className="h-[3px] w-1/2 rounded-full bg-background/90" />
            <span className="h-[2px] w-1/4 rounded-full bg-background/70" />
          </span>
        )}
        {id === 'frame' && (
          <span className="flex h-full w-full flex-col items-center justify-center gap-1 px-1.5">
            <span className={cn(photo, 'h-[62%] w-full')} />
            <span className={cn(bar, 'h-[3px] w-1/2')} />
            <span className={cn(bar, 'h-[2px] w-1/4 opacity-70')} />
          </span>
        )}
        {id === 'split' && (
          <span className="flex h-full w-full gap-1">
            <span className={cn(photo, 'h-full w-1/2')} />
            <span className="flex w-1/2 flex-col justify-center gap-1">
              <span className={cn(bar, 'h-[3px] w-full')} />
              <span className={cn(bar, 'h-[2px] w-2/3 opacity-70')} />
            </span>
          </span>
        )}
        {id === 'masthead' && (
          <span className="flex h-full w-full flex-col gap-1">
            <span className="flex flex-col items-center gap-1 pt-1">
              <span className={cn(bar, 'h-[3px] w-1/2')} />
              <span className={cn(bar, 'h-[2px] w-1/4 opacity-70')} />
            </span>
            <span className={cn(photo, 'w-full flex-1')} />
          </span>
        )}
      </span>
      <span className={cn('text-[11px] leading-tight', selected ? 'font-medium text-foreground' : 'text-muted-foreground')}>
        {COVER_LAYOUT_NAMES[id]}
      </span>
    </button>
  );
}

/** A background, as its own colour. Ringed so a white ground still
 *  reads as a control on a white card, and checked when chosen. */
function Swatch({
  selected, disabled, color, label, onClick,
}: {
  selected: boolean; disabled: boolean; color: string; label: string; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={selected}
      className={cn(
        'relative h-7 w-7 rounded-full transition-all disabled:opacity-40',
        'ring-1 ring-inset ring-foreground/15',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected && 'ring-2 ring-primary ring-offset-2 ring-offset-card',
      )}
      style={{ background: color }}
    >
      {selected && (
        <svg viewBox="0 0 24 24" className="absolute inset-0 m-auto h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: isLightHex(color) ? '#1a1a1a' : '#ffffff' }}>
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
    </button>
  );
}

/** Rough luminance, to decide whether a tick on this ground is ink or paper. */
function isLightHex(hex: string): boolean {
  const h = hex.replace('#', '');
  if (h.length !== 6) return true;
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 140;
}

function ChipRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {children}
    </div>
  );
}

/** A choice chip — the selected one is filled, like the app's other toggles. */
function Chip({
  selected, disabled, swatch, onClick, children,
}: {
  selected: boolean; disabled: boolean; swatch?: string; onClick: () => void; children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'inline-flex h-8 items-center gap-2 rounded-full border text-xs transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:pointer-events-none disabled:opacity-50',
        swatch ? 'pl-1.5 pr-3' : 'px-3',
        // UI-BLUE-1 — filled ONLY when selected, and the fill is blue.
        selected
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border/60 bg-transparent text-foreground hover:border-foreground/40',
      )}
    >
      {swatch && (
        // A mid-grey hairline reads on every swatch, on either chip.
        <span
          aria-hidden
          className="h-5 w-5 shrink-0 rounded-full"
          style={{ background: swatch, boxShadow: 'inset 0 0 0 1px rgba(128,128,128,.45)' }}
        />
      )}
      {children}
    </button>
  );
}

/**
 * Label and reading on one line, the slider under them — so the slider
 * keeps its length on a phone. Committed on release, like the
 * slideshow's, so the editor saves once per move rather than per tick.
 */
/**
 * Where the cover sits.
 *
 * Rennan chose full-bleed over a band that follows the photo, so the
 * cover IS a crop. This is what keeps it honest: the studio drags to say
 * what stays in frame, instead of every cover inheriting the 50% / 35%
 * that was hardcoded in the template. Pointer events, so it works with a
 * trackpad, a finger and a stylus without three code paths — and the
 * arrow keys move it a percent at a time for anyone not using a pointer.
 */
function FocalPicker({
  coverUrl, x, y, disabled, onChange,
}: {
  coverUrl?: string | null;
  x: number;
  y: number;
  disabled: boolean;
  onChange: (x: number, y: number) => void;
}) {
  const boxRef = useRef<HTMLDivElement>(null);

  const place = (clientX: number, clientY: number) => {
    const el = boxRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    onChange(
      Math.min(100, Math.max(0, Math.round(((clientX - r.left) / r.width) * 100))),
      Math.min(100, Math.max(0, Math.round(((clientY - r.top) / r.height) * 100))),
    );
  };

  return (
    <div className="max-w-xs space-y-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs text-foreground">Cover focus</p>
        <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
          {x}% · {y}%
        </span>
      </div>
      <div
        ref={boxRef}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label="Where the cover photo is anchored"
        aria-valuetext={`${x} percent across, ${y} percent down`}
        aria-valuenow={x}
        aria-valuemin={0}
        aria-valuemax={100}
        onPointerDown={(e) => {
          if (disabled) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          place(e.clientX, e.clientY);
        }}
        onPointerMove={(e) => {
          if (disabled || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
          place(e.clientX, e.clientY);
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          const d = e.shiftKey ? 10 : 1;
          if (e.key === 'ArrowLeft')  { e.preventDefault(); onChange(Math.max(0, x - d), y); }
          if (e.key === 'ArrowRight') { e.preventDefault(); onChange(Math.min(100, x + d), y); }
          if (e.key === 'ArrowUp')    { e.preventDefault(); onChange(x, Math.max(0, y - d)); }
          if (e.key === 'ArrowDown')  { e.preventDefault(); onChange(x, Math.min(100, y + d)); }
        }}
        className={cn(
          'relative w-full overflow-hidden rounded-lg border border-border/60 bg-muted',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          disabled ? 'pointer-events-none opacity-50' : 'cursor-crosshair touch-none',
        )}
        style={{ aspectRatio: '16 / 9' }}
      >
        {coverUrl ? (
          <img
            src={coverUrl}
            alt=""
            draggable={false}
            className="pointer-events-none absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: `${x}% ${y}%` }}
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <p className="px-4 text-center text-[11px] text-muted-foreground">
              Add a photo and its cover shows here.
            </p>
          </div>
        )}
        {/* The mark. Two rings so it reads on a dark frame and a light one. */}
        <span
          aria-hidden
          className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1.5px_rgba(0,0,0,0.5)]"
          style={{ left: `${x}%`, top: `${y}%` }}
        />
      </div>
      <p className="text-[11px] text-muted-foreground">
        Drag to choose what stays in frame when the cover is cropped to the page.
      </p>
    </div>
  );
}

function SliderRow({
  label, value, min, max, readout, disabled, onDraft, onCommit,
}: {
  label: string; value: number; min: number; max: number; readout: string; disabled: boolean;
  onDraft: (v: number | undefined) => void; onCommit: (v: number | undefined) => void;
}) {
  return (
    <div className="max-w-xs space-y-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs text-foreground">{label}</p>
        <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{readout}</span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={COVER_STEP}
        disabled={disabled}
        onValueChange={(v) => onDraft(v[0])}
        onValueCommit={(v) => onCommit(v[0])}
        aria-label={label}
      />
    </div>
  );
}

function FontPick({
  label, value, defaultFamily, options, disabled, onChange,
}: {
  label: string; value: string | null; defaultFamily: string;
  // `readonly` because DISPLAY_FONTS / BODY_FONTS are the `as const`
  // whitelist from templates/types.ts — the picker and the security
  // check read the same frozen list, which is the point (SEC-XSS).
  options: readonly string[]; disabled: boolean; onChange: (v: string | null) => void;
}) {
  // GALLERY-2 — a pairing can set a face that IS the design's own (Basic's
  // Serif keeps Figtree for text). The list leaves the design's own face
  // out (it's the default item), so show that as the default rather than
  // as an empty trigger.
  const shown = value && value !== defaultFamily ? value : DEFAULT;
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={shown} onValueChange={(v) => onChange(v === DEFAULT ? null : v)} disabled={disabled}>
        <SelectTrigger className="h-9 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={DEFAULT}>Design default · {defaultFamily}</SelectItem>
          {options.filter((o) => o !== defaultFamily).map((o) => (
            <SelectItem key={o} value={o}>
              <span style={{ fontFamily: `'${o}', sans-serif` }}>{o}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default GalleryCustomizeControls;
